import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { cx } from '@emotion/css';
import { getSessionStorageItem, setSessionStorageItem } from '@revenuemarket/browser';
import { Flex } from '@revenuemarket/cds';
import { theme } from '@revenuemarket/design';
import { convert, mixpanelTrack } from '@revenuemarket/utils';
import { DatePicker, Segmented, Tag } from 'antd';
import { type RangePickerProps } from 'antd/lib/date-picker/generatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { RangeInfo } from 'rc-picker/lib/RangePicker';
import { PickerMode, PresetDate, type RangeValue } from 'rc-picker/lib/interface';
import { P, match } from 'ts-pattern';

import { DateRangePreset } from '~apis/analysis';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import 'dayjs/locale/ko';

export type DatePickerSize = 'small' | 'middle' | 'large';
export type DatePickerRangeType = Exclude<PickerMode, 'time' | 'decade'>;

type Value = [Dayjs?, Dayjs?];

interface Props {
  direction?: 'row' | 'column';
  size?: DatePickerSize;
  rangeType?: DatePickerRangeType;
  onChangeRangeType?: (rangeType: DatePickerRangeType) => void;
  value?: Value;
  showType?: boolean;
  onPresetChange?: (value: DateRangePreset) => void;
  // DateRange 변경이 확정 되었을 때
  onDateRangeCompleted?: (tempChangedValues: Value) => void;
  memoizedKeyValue?: string;
  datePickerProps?: Omit<RangePickerProps<Dayjs>, 'value' | 'onChange'>;
  /**
   * 미래 날짜 허용
   */
  allowFutureDate?: boolean;
  className?: string;
}

const DateRangePickerWithPreset = ({
  direction = 'row',
  rangeType = 'month',
  size = 'small',
  onChangeRangeType,
  value,
  onPresetChange,
  onDateRangeCompleted,
  showType = true,
  datePickerProps,
  memoizedKeyValue,
  allowFutureDate = false,
  className,
}: Props) => {
  const tempRangeDateValue = useRef<Value>([]);

  const presets = match(rangeType)
    .with('date', () => DailyPresets)
    .with('week', () => WeeklyPresets)
    .with('month', () => MonthlyPresets)
    .with('quarter', () => QuarterlyPresets)
    .with('year', () => YearlyPresets)
    .exhaustive();

  const currentPreset = useMemo(() => {
    const [startDate, endDate] = value ?? [];
    if (startDate == null || endDate == null) {
      return undefined;
    }

    return presets.find(
      (preset) =>
        preset.value?.[0]?.isSame(startDate, 'days') && preset.value?.[1]?.isSame(endDate, 'days'),
    );
  }, [value, presets]);

  useEffect(() => {
    if (value) {
      const key = window.location.pathname;

      setSessionStorageItem(`preset-value${memoizedKeyValue || key}`, [
        value[0]?.toISOString(),
        value[1]?.toISOString(),
      ]);
    }
  }, [value, memoizedKeyValue]);

  useEffect(() => {
    if (rangeType) {
      const key = window.location.pathname;

      setSessionStorageItem(`preset-rangeType${memoizedKeyValue || key}`, rangeType);
    }
  }, [rangeType, memoizedKeyValue]);

  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    setRendered(true);
  }, [rendered]);

  const CommonSegmented = ({ block, style }: { block?: boolean; style?: React.CSSProperties }) => {
    return (
      <Segmented
        block={block}
        style={style}
        size={size}
        value={rangeType}
        options={[
          {
            label: '일',
            value: 'date',
          },
          {
            label: '주',
            value: 'week',
          },
          {
            label: '월',
            value: 'month',
          },
          {
            label: '분기',
            value: 'quarter',
          },
          {
            label: '연',
            value: 'year',
          },
        ]}
        onChange={(rangeType) => {
          const currentValue = value || [dayjs(), dayjs()];
          const from = tempRangeDateValue.current[0] || currentValue[0] || dayjs();

          // from 날짜 기준으로 새 rangeType의 시작과 끝을 계산
          // (분기/연에서 월로 변경 시 from 날짜가 속한 월만 선택되도록)
          const newEndDate = from.endOf(rangeType as DatePickerRangeType);

          tempRangeDateValue.current = [
            from.startOf(rangeType as DatePickerRangeType),
            allowFutureDate ? newEndDate : newEndDate.isAfter(dayjs()) ? dayjs() : newEndDate,
          ];

          onDateRangeCompleted?.(tempRangeDateValue.current);
          onChangeRangeType?.(rangeType as DatePickerRangeType);
        }}
        className={cx(
          css`
            background-color: ${theme.colorTokens.bgWeak};
          `,
          className,
        )}
      />
    );
  };

  const CommonDatePicker = () => (
    <div>
      <DatePicker.RangePicker
        size={size}
        picker={rangeType}
        format={FormatByRangeType[rangeType]}
        value={convertValueToDatePickerValue(value)}
        onChange={(value) => {
          const result = convertDatePickerValueToValue(value, rangeType);

          const presetKey = presets.find(
            (preset) =>
              preset.value?.[0]?.isSame(result[0], 'days') &&
              preset.value?.[1]?.isSame(result[1], 'days'),
          )?.key;

          onPresetChange?.(presetKey as DateRangePreset);

          mixpanelTrack(MIXPANEL_EVENT.DATE_CHANGE.DEFAULT, {
            firstDate: result[0]?.format('YYYY-MM-DD') ?? '',
            lastDate: result[1]?.format('YYYY-MM-DD') ?? '',
          });

          mixpanelTrack(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
            type: 'change date',
          });

          tempRangeDateValue.current = result;
        }}
        onCalendarChange={(value, _, info: RangeInfo) => {
          const result = convertDatePickerValueToValue(value, rangeType);
          tempRangeDateValue.current = result;
        }}
        onOpenChange={(open) => {
          if (
            !open &&
            tempRangeDateValue.current?.[0] != null &&
            tempRangeDateValue.current?.[1] != null
          ) {
            onDateRangeCompleted?.(tempRangeDateValue.current);
          }
        }}
        presets={presets}
        {...datePickerProps}
        className={cx(
          css`
            .ant-picker-input > input {
              width: ${match(rangeType)
                .with(P.union('date', 'week'), () => '72px')
                .with(P.union('month', 'quarter'), () => '56px')
                .with(P.union('year'), () => '32px')
                .exhaustive()};
            }

            .ant-picker-suffix {
              margin-inline-start: ${match(rangeType)
                .with(P.union('date', 'week'), () => '4px')
                .with(P.union('month', 'quarter', 'year'), () => '2px')
                .exhaustive()};
            }
          `,
          datePickerProps?.className,
        )}
      />
    </div>
  );

  const Preset = () => {
    return currentPreset && <Tag>{currentPreset.label}</Tag>;
  };

  return rendered ? (
    direction === 'row' ? (
      <Flex direction={direction} gap={10} align="center">
        {showType && <CommonSegmented />}
        <Flex gap={10} align="center">
          <CommonDatePicker />
          <Preset />
        </Flex>
      </Flex>
    ) : (
      <Flex direction="column" gap={4}>
        <Flex gap={4} align="center">
          {showType && <CommonSegmented style={{ flex: 1 }} block />}
          <Preset />
        </Flex>
        <CommonDatePicker />
      </Flex>
    )
  ) : (
    <div />
  );
};

export default memo(DateRangePickerWithPreset);

export function convertValueToDatePickerValue(value?: Value): RangeValue<Dayjs> {
  if (value == null) {
    return null;
  }

  const [startDate, endDate] = value;
  return [startDate ?? null, endDate ?? null];
}

function convertDatePickerValueToValue(
  value: RangeValue<Dayjs>,
  rangeType: DatePickerRangeType,
): Value {
  if (value == null) {
    return [undefined, undefined];
  }

  const [startDate, endDate] = value;

  return [
    convert(startDate, (startDate) => {
      return dayjs(startDate).startOf(rangeType);
    }) ?? undefined,
    convert(endDate, (endDate) => {
      return dayjs(endDate).endOf(rangeType);
    }) ?? undefined,
  ];
}

export const FormatByRangeType: Record<DatePickerRangeType, string> = {
  date: 'YYYY-MM-DD',
  month: 'YYYY-MM',
  week: 'YYYY-MM-DD 주',
  quarter: 'YYYY-[Q]Q',
  year: 'YYYY',
};

export const getMemoizedPreset = (memoizedKeyValue?: string) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const key = window.location.pathname;
  const value = getSessionStorageItem(`preset-value${memoizedKeyValue || key}`) as [string, string];
  if (!value) {
    return null;
  }
  const defaultSavedValue = [dayjs(value[0]), dayjs(value[1])] as [dayjs.Dayjs, dayjs.Dayjs];

  const rangeType = getSessionStorageItem(
    `preset-rangeType${memoizedKeyValue || key}`,
  ) as DatePickerRangeType;

  if (rangeType !== 'date') {
    switch (rangeType) {
      case 'month': // 현재와 같은 달인 경우에는 오늘까지만 선택되도록
        return {
          value: defaultSavedValue[1].isSame(dayjs(), 'month')
            ? ([defaultSavedValue[0].startOf('month'), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs])
            : defaultSavedValue,
          rangeType,
        };
      case 'week':
        return {
          value: defaultSavedValue[1].isSame(dayjs(), 'week')
            ? ([defaultSavedValue[0].startOf('week'), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs])
            : defaultSavedValue,
          rangeType,
        };
      case 'quarter':
        return {
          value: defaultSavedValue[1].isSame(dayjs(), 'quarter')
            ? ([defaultSavedValue[0].startOf('quarter'), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs])
            : defaultSavedValue,
          rangeType,
        };
      case 'year':
        return {
          value: defaultSavedValue[1].isSame(dayjs(), 'year')
            ? ([defaultSavedValue[0].startOf('year'), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs])
            : defaultSavedValue,
          rangeType,
        };
    }
  }

  return value && rangeType
    ? {
        rangeType,
        value: defaultSavedValue,
      }
    : null;
};

export const DailyPresets: (PresetDate<Required<Value>> & { key: DateRangePreset })[] = [
  {
    key: DateRangePreset.TODAY,
    label: '오늘',
    value: [dayjs(), dayjs()],
  },
  {
    key: DateRangePreset.YESTERDAY,
    label: '어제',
    value: [dayjs().subtract(1, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    key: DateRangePreset.LAST_7_DAYS,
    label: '최근 7일',
    value: [dayjs().subtract(7, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    key: DateRangePreset.LAST_30_DAYS,
    label: '최근 30일',
    value: [dayjs().subtract(30, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    key: DateRangePreset.LAST_60_DAYS,
    label: '최근 60일',
    value: [dayjs().subtract(60, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    key: DateRangePreset.LAST_90_DAYS,
    label: '최근 90일',
    value: [dayjs().subtract(90, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    key: DateRangePreset.THIS_WEEK,
    label: '이번 주',
    value: [dayjs().startOf('week'), dayjs()],
  },
  {
    key: DateRangePreset.THIS_MONTH,
    label: '이번 달',
    value: [dayjs().startOf('month'), dayjs()],
  },
  {
    key: DateRangePreset.THIS_QUARTER,
    label: '이번 분기',
    value: [dayjs().startOf('quarter'), dayjs()],
  },
  {
    key: DateRangePreset.THIS_YEAR,
    label: '올해',
    value: [dayjs().startOf('year'), dayjs()],
  },
  {
    key: DateRangePreset.LAST_YEAR,
    label: '작년',
    value: [
      dayjs().subtract(1, 'years').startOf('years'),
      dayjs().subtract(1, 'years').endOf('years'),
    ],
  },
];

export const WeeklyPresets: (PresetDate<Required<Value>> & { key: string })[] = [
  {
    key: DateRangePreset.THIS_WEEK,
    label: '이번 주',
    value: [dayjs().startOf('week'), dayjs()],
  },
  {
    key: DateRangePreset.LAST_WEEK,
    label: '지난 주',
    value: [dayjs().subtract(1, 'week').startOf('week'), dayjs().subtract(1, 'week').endOf('week')],
  },
  {
    key: DateRangePreset.LAST_2_WEEKS,
    label: '지난 2주',
    value: [dayjs().subtract(2, 'week').startOf('week'), dayjs().subtract(1, 'week').endOf('week')],
  },
  {
    key: DateRangePreset.LAST_4_WEEKS,
    label: '지난 4주',
    value: [dayjs().subtract(4, 'week').startOf('week'), dayjs().subtract(1, 'week').endOf('week')],
  },
  {
    key: DateRangePreset.LAST_12_WEEKS,
    label: '지난 12주',
    value: [
      dayjs().subtract(12, 'week').startOf('week'),
      dayjs().subtract(1, 'week').endOf('week'),
    ],
  },
  {
    key: DateRangePreset.THIS_MONTH,
    label: '이번 달',
    value: [dayjs().startOf('month'), dayjs()],
  },
  {
    key: DateRangePreset.THIS_QUARTER,
    label: '이번 분기',
    value: [dayjs().startOf('quarter'), dayjs()],
  },
  {
    key: DateRangePreset.THIS_YEAR,
    label: '올해',
    value: [dayjs().startOf('year'), dayjs()],
  },
  {
    key: DateRangePreset.LAST_YEAR,
    label: '작년',
    value: [
      dayjs().subtract(1, 'years').startOf('years'),
      dayjs().subtract(1, 'years').endOf('years'),
    ],
  },
];

export const MonthlyPresets: (PresetDate<Required<Value>> & { key: string })[] = [
  {
    key: DateRangePreset.THIS_MONTH,
    label: '이번 달',
    value: [dayjs().startOf('month'), dayjs()],
  },
  {
    key: DateRangePreset.LAST_MONTH,
    label: '지난 달',
    value: [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
  },
  {
    key: DateRangePreset.LAST_3_MONTHS,
    label: '지난 3개월',
    value: [
      dayjs().subtract(3, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
  },
  {
    key: DateRangePreset.LAST_6_MONTHS,
    label: '지난 6개월',
    value: [
      dayjs().subtract(6, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
  },
  {
    key: DateRangePreset.THIS_QUARTER,
    label: '이번 분기',
    value: [dayjs().startOf('quarter'), dayjs()],
  },
  {
    key: DateRangePreset.THIS_YEAR,
    label: '올해',
    value: [dayjs().startOf('year'), dayjs()],
  },
  {
    key: DateRangePreset.LAST_YEAR,
    label: '작년',
    value: [
      dayjs().subtract(1, 'years').startOf('years'),
      dayjs().subtract(1, 'years').endOf('years'),
    ],
  },
];

export const QuarterlyPresets: (PresetDate<Required<Value>> & { key: string })[] = [
  {
    key: DateRangePreset.THIS_QUARTER,
    label: '이번 분기',
    value: [dayjs().startOf('quarter'), dayjs()],
  },
  {
    key: DateRangePreset.LAST_QUARTER,
    label: '지난 분기',
    value: [
      dayjs().subtract(1, 'quarter').startOf('quarter'),
      dayjs().subtract(1, 'quarter').endOf('quarter'),
    ],
  },
  {
    key: DateRangePreset.LAST_2_QUARTERS,
    label: '지난 2분기',
    value: [
      dayjs().subtract(2, 'quarter').startOf('quarter'),
      dayjs().subtract(1, 'quarter').endOf('quarter'),
    ],
  },
  {
    key: DateRangePreset.THIS_YEAR,
    label: '올해',
    value: [dayjs().startOf('year'), dayjs()],
  },
  {
    key: DateRangePreset.LAST_YEAR,
    label: '작년',
    value: [
      dayjs().subtract(1, 'years').startOf('years'),
      dayjs().subtract(1, 'years').endOf('years'),
    ],
  },
];

export const YearlyPresets: (PresetDate<Required<Value>> & { key: string })[] = [
  {
    key: DateRangePreset.THIS_YEAR,
    label: '올해',
    value: [dayjs().startOf('years'), dayjs()],
  },
  {
    key: DateRangePreset.LAST_YEAR,
    label: '작년',
    value: [
      dayjs().subtract(1, 'years').startOf('years'),
      dayjs().subtract(1, 'years').endOf('years'),
    ],
  },
  {
    key: DateRangePreset.LAST_2_YEARS,
    label: '지난 2년',
    value: [
      dayjs().subtract(2, 'years').startOf('years'),
      dayjs().subtract(1, 'years').endOf('years'),
    ],
  },
];
